import { useLingui } from "@lingui/react";
import type { ElevenLabsVoiceOptions } from "@repo/common";
import { listElevenLabsVoiceOptions } from "@repo/ui";
import { useEffect, useState } from "react";
import type { Agent } from "../types";
import { Button } from "./ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";

export function ElevenLabsVoiceFields({
	agent,
	onChange,
}: {
	agent: Agent;
	onChange: (agent: Agent) => void;
}) {
	const { i18n } = useLingui();
	const [options, setOptions] = useState<ElevenLabsVoiceOptions>();
	const [failed, setFailed] = useState(false);
	const [attempt, setAttempt] = useState(0);
	// biome-ignore lint/correctness/useExhaustiveDependencies: attempt explicitly retries the failed catalog request.
	useEffect(() => {
		let active = true;
		setOptions(undefined);
		setFailed(false);
		listElevenLabsVoiceOptions({ companyId: agent.companyId }).then(
			(result) => {
				if (active) setOptions(result.data);
			},
			() => {
				if (active) setFailed(true);
			},
		);
		return () => {
			active = false;
		};
	}, [agent.companyId, attempt]);
	const placeholder = i18n._({
		id: "agentEditor.elevenlabs.choose",
		message: "Choose an option",
	});
	return (
		<>
			<div className="w-full space-y-2 sm:w-[240px]">
				<label htmlFor="agent-voice" className="text-sm font-medium">
					{i18n._("agentEditor.fields.voice")} · ElevenLabs
				</label>
				<Select
					value={agent.voice || ""}
					disabled={!options}
					onValueChange={(voice) => onChange({ ...agent, voice })}
				>
					<SelectTrigger id="agent-voice">
						<SelectValue placeholder={placeholder} />
					</SelectTrigger>
					<SelectContent>
						{agent.voice &&
							!options?.voices.some((voice) => voice.id === agent.voice) && (
								<SelectItem value={agent.voice} disabled>
									{agent.voice} (
									{i18n._({
										id: "agentEditor.elevenlabs.unavailableSelection",
										message: "Unavailable",
									})}
									)
								</SelectItem>
							)}
						{options?.voices.map((voice) => (
							<SelectItem key={voice.id} value={voice.id}>
								{voice.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			<div className="w-full space-y-2 sm:w-[200px]">
				<label htmlFor="agent-language" className="text-sm font-medium">
					{i18n._("agentEditor.fields.defaultLanguage")}
				</label>
				<Select
					value={agent.defaultLanguage || ""}
					disabled={!options}
					onValueChange={(defaultLanguage) =>
						onChange({ ...agent, defaultLanguage })
					}
				>
					<SelectTrigger id="agent-language">
						<SelectValue placeholder={placeholder} />
					</SelectTrigger>
					<SelectContent>
						{agent.defaultLanguage &&
							!options?.languages.some(
								(language) => language.code === agent.defaultLanguage,
							) && (
								<SelectItem value={agent.defaultLanguage} disabled>
									{agent.defaultLanguage} (
									{i18n._({
										id: "agentEditor.elevenlabs.unavailableSelection",
										message: "Unavailable",
									})}
									)
								</SelectItem>
							)}
						{options?.languages.map((language) => (
							<SelectItem key={language.code} value={language.code}>
								{new Intl.DisplayNames([i18n.locale || "en"], {
									type: "language",
								}).of(language.code) || language.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			{!options && (
				<div role="status" className="w-full text-sm text-muted-foreground">
					{failed ? (
						<>
							{i18n._({
								id: "agentEditor.elevenlabs.unavailable",
								message: "Could not load ElevenLabs voices and languages.",
							})}
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => setAttempt((value) => value + 1)}
							>
								{i18n._({
									id: "agentEditor.elevenlabs.retry",
									message: "Try again",
								})}
							</Button>
						</>
					) : (
						i18n._({
							id: "agentEditor.elevenlabs.loading",
							message: "Loading ElevenLabs voices and languages…",
						})
					)}
				</div>
			)}
		</>
	);
}
